import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
} from "react";
import Parse from "parse";
import { TextContent } from "../types";
import { useTranslation } from "@opendash/i18n";
import { TENANT_ID } from "../const";

interface TextsContextType {
  texts: Record<string, TextContent>;
  loading: boolean;
  t: (key: string, fallback?: string) => string;
  getKeysWithPrefix: (prefix: string) => string[];
}

const TextsContext = createContext<TextsContextType>({
  texts: {},
  loading: true,
  t: (key, fallback) => fallback || key,
  getKeysWithPrefix: () => [],
});

/**
 * Provider zum Laden aller Texte für den Tenant
 */
export function TextsProvider({ children }: { children: ReactNode }) {
  const [texts, setTexts] = useState<Record<string, TextContent>>({});
  const [loading, setLoading] = useState(true);
  const { language } = useTranslation();

  useEffect(() => {
    async function loadTexts() {
      try {
        const Tenant = Parse.Object.extend("OD3_Tenant");
        const tenant = new Tenant();
        tenant.id = TENANT_ID;

        const query = new Parse.Query<TextContent>(TextContent);
        query.equalTo("tenant", tenant);
        query.limit(1000);

        const results = await query.find();

        const textsMap: Record<string, TextContent> = {};
        results.forEach((text) => {
          textsMap[text.key] = text;
        });

        setTexts(textsMap);
      } catch (err) {
        console.error("Fehler beim Laden der Texte:", err);
      } finally {
        setLoading(false);
      }
    }

    loadTexts();
  }, []);

  /**
   * Text nach Key abrufen
   * @param key - z.B. "wasserherkunft.grundwasser.title"
   * @param fallback - Fallback-Text wenn Key nicht gefunden
   */
  const t = (key: string, fallback?: string): string => {
    const text = texts[key];
    if (!text) return fallback || key;

    // Aktuelle Sprache aus i18n verwenden
    const lang = language || "de";
    const langKey = `value_${lang}` as keyof typeof text.attributes;
    return (
      (text.get(langKey) as string) || text.get("value_de") || fallback || key
    );
  };

  /**
   * Alle Keys finden, die mit einem Präfix beginnen
   * @param prefix - z.B. "B.1.literatur."
   */
  const getKeysWithPrefix = (prefix: string): string[] => {
    return Object.keys(texts)
      .filter((key) => key.startsWith(prefix))
      .sort();
  };

  return (
    <TextsContext.Provider value={{ texts, loading, t, getKeysWithPrefix }}>
      {children}
    </TextsContext.Provider>
  );
}

/**
 * Hook zum Abrufen der t()-Funktion
 */
export function useTexts() {
  return useContext(TextsContext);
}
